import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Play, Trophy, Shield, Zap, ChevronRight } from 'lucide-react'

export default function LandingPage() {
  const navigate = useNavigate()

  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-oded-dark via-oded-dark to-[#0d1a2d]">
          <div className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `radial-gradient(circle at 50% 50%, rgba(0,212,255,0.15) 0%, transparent 50%)`,
            }}
          />
        </div>
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-oded-accent rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          {/* Logo */}
          <div className="mb-8">
            <h1 className="text-8xl font-bold tracking-wider neon-text">
              ODED
            </h1>
            <div className="mt-2 flex items-center justify-center gap-2">
              <div className="h-[2px] w-16 bg-gradient-to-r from-transparent to-oded-accent" />
              <span className="text-oded-accent/80 text-sm tracking-[0.3em] uppercase">
                Battle Royale
              </span>
              <div className="h-[2px] w-16 bg-gradient-to-l from-transparent to-oded-accent" />
            </div>
          </div>

          {/* Tagline */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="text-xl text-white/60 mb-12 max-w-lg mx-auto"
          >
            Drop in. Loot up. Survive. The ultimate 3D Battle Royale experience in your browser.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => navigate('/play')}
              className="btn-primary flex items-center gap-2 text-lg px-8 py-4"
            >
              <Play size={20} />
              PLAY NOW
              <ChevronRight size={20} />
            </button>
            <button
              onClick={() => navigate('/leaderboard')}
              className="btn-secondary flex items-center gap-2 text-lg px-8 py-4"
            >
              <Trophy size={20} />
              LEADERBOARD
            </button>
          </motion.div>

          {/* Auth buttons */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="mt-8 flex gap-4 justify-center"
          >
            <button
              onClick={() => navigate('/login')}
              className="text-white/60 hover:text-oded-accent transition-colors"
            >
              Sign In
            </button>
            <span className="text-white/30">|</span>
            <button
              onClick={() => navigate('/register')}
              className="text-white/60 hover:text-oded-accent transition-colors"
            >
              Create Account
            </button>
          </motion.div>
        </motion.div>

        {/* Features */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8 }}
          className="absolute bottom-12 flex gap-8"
        >
          {[
            { icon: Zap, label: '3D Graphics', desc: 'Three.js powered' },
            { icon: Shield, label: 'AI Bots', desc: 'Smart enemies' },
            { icon: Trophy, label: 'Ranked', desc: 'Global leaderboard' },
          ].map((feature, i) => (
            <div key={i} className="flex items-center gap-3 text-white/50">
              <feature.icon size={20} className="text-oded-accent" />
              <div>
                <div className="text-sm font-semibold text-white/70">{feature.label}</div>
                <div className="text-xs">{feature.desc}</div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </div>
  )
}